import { addMonths } from "date-fns/addMonths";
import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { isFuture } from "date-fns/isFuture";
import { isPast } from "date-fns/isPast";
import { subDays } from "date-fns/subDays";

import type {
  PlanPeriod,
  Product,
} from "../types/schema/subscription/productSchema";
import type { SubscriptionDetail } from "../types/schema/subscription/subscriptionSchema";

export const isMobileSubscription = (
  subscription?: SubscriptionDetail,
): boolean =>
  isApple(subscription) || isGoogle(subscription) || isAmazon(subscription);

export const isWebSubscription = (subscription?: SubscriptionDetail): boolean =>
  isStripe(subscription) ||
  isPayPal(subscription) ||
  isBilling(subscription) ||
  isHotmart(subscription);

export const isFree = (subscription?: SubscriptionDetail): boolean => {
  return (
    subscription?.type === "hallow" &&
    (!subscription.price || subscription.price.amount === 0)
  );
};

export const isLifetime = (subscription?: SubscriptionDetail): boolean => {
  return subscription.product === "lifetime_full_subscription";
};

export const isStripe = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "stripe";
};

export const isBilling = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "billing";
};

export const isHotmart = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "hotmart";
};

export const isPayPal = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "paypal";
};

export const isGoogle = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "google";
};

export const isApple = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "apple";
};

export const isAmazon = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.type === "amazon";
};

type IsFamilyPlan = {
  (type: "product", product?: Product): boolean;
  (type: "subscription", subscription?: SubscriptionDetail): boolean;
};
export const isFamilyPlan: IsFamilyPlan = (type, obj): boolean => {
  if (type === "product") {
    return obj?.external_id === "yearly_family_subscription";
  }
  return obj?.product === "yearly_family_subscription";
};

export const isFamilyOwner = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.is_owner === true;
};

export const hasFamilyInvite = () => {
  return !!window.sessionStorage.getItem("inviteCode");
};

export const isModifiable = (subscription?: SubscriptionDetail): boolean => {
  return (
    !isFamilyPlan("subscription", subscription) &&
    !isFree(subscription) &&
    !isBilling(subscription) &&
    !isHotmart(subscription) &&
    !isLifetime(subscription)
  );
};

export const isCancellable = (subscription?: SubscriptionDetail): boolean => {
  return (
    isWebSubscription(subscription) &&
    !isFree(subscription) &&
    !isLifetime(subscription) &&
    isFamilyOwner(subscription) &&
    isRenewing(subscription)
  );
};

export const isRestartable = (subscription?: SubscriptionDetail): boolean => {
  return (
    isWebSubscription(subscription) &&
    !isFree(subscription) &&
    !isLifetime(subscription) &&
    isFamilyOwner(subscription) &&
    !isRenewing(subscription) &&
    !!subscription.price_id
  );
};

export const isCurrentProduct = (
  subscription: SubscriptionDetail,
  product: Product,
): boolean => {
  return product.external_id === subscription.product;
};

export const currentProduct = (
  subscription: SubscriptionDetail,
  products: Product[],
): Product | undefined => {
  return products.find((product) => isCurrentProduct(subscription, product));
};

export const periodToDays = (period: PlanPeriod, date?: Date): number => {
  switch (period.unit) {
    case "day":
      return period.number;
    case "week":
      return period.number * 7;
    case "month":
      return date
        ? differenceInCalendarDays(addMonths(date, period.number), date)
        : period.number * 30;
    case "year":
      return period.number * 365;
    default:
      return 0;
  }
};

export const isLongerPeriod = (
  product1: Product,
  product2: Product,
): boolean => {
  if (!product1 || !product2) return false;
  return (
    periodToDays(product1.billing_period) >
    periodToDays(product2.billing_period)
  );
};

export const hasExpired = (subscription?: SubscriptionDetail): boolean => {
  if (!subscription) return false;
  return isPast(new Date(subscription?.end_date));
};

export const hasStarted = (subscription?: SubscriptionDetail): boolean => {
  if (!subscription) return false;
  return isPast(new Date(subscription.start_date));
};

export const isInTrial = (subscription?: SubscriptionDetail): boolean => {
  return (
    subscription?.is_trial &&
    subscription?.trial_ends_at &&
    isFuture(new Date(subscription.trial_ends_at))
  );
};

export const isRenewing = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.is_renewing;
};

export const inBillingRetry = (subscription?: SubscriptionDetail): boolean => {
  return subscription?.billing_issues_at && isRenewing(subscription);
};

export const hasAnyBillingIssues = (
  subscriptions: SubscriptionDetail[],
): boolean => {
  return subscriptions?.some((sub) => inBillingRetry(sub));
};

export const hasMultipleRenewing = (
  subscriptions: SubscriptionDetail[],
): boolean => {
  return subscriptions?.filter((sub) => sub.is_renewing).length > 1;
};

export const currentStatus = (
  subscription?: SubscriptionDetail,
): "active" | "trial" | "cancelled" => {
  if (!isRenewing(subscription)) return "cancelled";
  if (isInTrial(subscription)) return "trial";
  return "active";
};

export const nextPaymentDate = (
  subscription?: SubscriptionDetail,
): Date | undefined => {
  const date = subscription?.end_date;
  if (!date) return;
  return subDays(new Date(date), 1);
};

export const monthlyPriceOfYearly = (price: number): number => {
  // Calculate the price over twelve months
  const monthlyPrice = price / 12;

  // If the price is a whole number, round down to closest .99 when
  // available
  const roundedMonthlyPrice = Math.round(monthlyPrice * 100) / 100;
  if (roundedMonthlyPrice % 1 === 0) return Math.max(0, monthlyPrice - 0.01);
  return monthlyPrice;
};

export const isDefaultPlan = (product: Product): boolean => {
  return (
    product.billing_period.unit === "year" &&
    product.billing_period.number === 1 &&
    !isFamilyPlan("product", product)
  );
};
